import { create } from 'zustand';
import { refId } from '@/lib/api/refs';
import type { Alert, Id, PositionEvent } from '@/types/api';

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'offline';

export interface LivePosition {
  vehicleId: Id;
  plateNumber: string;
  lat: number;
  lng: number;
  speedKph: number;
  headingDeg: number;
  ignition: boolean;
  /** Device clock — what the ping claims. */
  timestamp: string;
  /** Browser clock at arrival — what "12s ago" is measured against. */
  receivedAt: number;
}

interface LiveState {
  positions: Record<Id, LivePosition>;
  /** Vehicle ids with at least one unresolved alert, for the red marker state. */
  alerting: Record<Id, true>;
  connection: ConnectionStatus;
  lastEventAt: number | null;
  /** Monotonic counter so map layers can subscribe to "something moved". */
  revision: number;

  applyPosition: (event: PositionEvent) => void;
  seedPositions: (positions: LivePosition[]) => void;
  setConnection: (status: ConnectionStatus) => void;
  markAlerting: (vehicleIds: Id[]) => void;
  clearAlerting: (vehicleId: Id) => void;
  ingestAlert: (alert: Alert) => void;
  reset: () => void;
}

export const useLiveStore = create<LiveState>((set) => ({
  positions: {},
  alerting: {},
  connection: 'idle',
  lastEventAt: null,
  revision: 0,

  applyPosition: (event) =>
    set((state) => {
      const previous = state.positions[event.vehicleId];
      // Buffered devices replay old fixes on reconnect; never move a marker
      // backwards in time.
      if (previous && Date.parse(previous.timestamp) > Date.parse(event.timestamp)) {
        return state;
      }
      return {
        positions: {
          ...state.positions,
          [event.vehicleId]: {
            vehicleId: event.vehicleId,
            plateNumber: event.plateNumber,
            lat: event.lat,
            lng: event.lng,
            speedKph: event.speedKph ?? 0,
            headingDeg: event.headingDeg ?? previous?.headingDeg ?? 0,
            ignition: event.ignition ?? previous?.ignition ?? false,
            timestamp: event.timestamp,
            receivedAt: Date.now(),
          },
        },
        lastEventAt: Date.now(),
        revision: state.revision + 1,
      };
    }),

  seedPositions: (positions) =>
    set((state) => {
      const next = { ...state.positions };
      for (const position of positions) {
        const previous = next[position.vehicleId];
        // A live socket update always beats the REST snapshot it raced.
        if (previous && Date.parse(previous.timestamp) >= Date.parse(position.timestamp)) continue;
        next[position.vehicleId] = position;
      }
      return { positions: next, revision: state.revision + 1 };
    }),

  setConnection: (connection) => set({ connection }),

  markAlerting: (vehicleIds) =>
    set((state) => {
      const alerting: Record<Id, true> = {};
      for (const id of vehicleIds) alerting[id] = true;
      return { alerting, revision: state.revision + 1 };
    }),

  clearAlerting: (vehicleId) =>
    set((state) => {
      if (!state.alerting[vehicleId]) return state;
      const alerting = { ...state.alerting };
      delete alerting[vehicleId];
      return { alerting, revision: state.revision + 1 };
    }),

  ingestAlert: (alert) =>
    set((state) => {
      const vehicleId = refId(alert.vehicleId);
      if (!vehicleId || alert.status === 'RESOLVED') return state;
      if (state.alerting[vehicleId]) return state;
      return {
        alerting: { ...state.alerting, [vehicleId]: true },
        revision: state.revision + 1,
      };
    }),

  reset: () =>
    set({ positions: {}, alerting: {}, connection: 'idle', lastEventAt: null, revision: 0 }),
}));
